import { create } from 'zustand';
import { stockAPI, type StockShortageDto } from '@matix/api-client';
import type { CartLine } from './cart';
import { useTenantConfig } from './tenantConfig';

/**
 * What is on the shelf for the items in the cart — the till's copy of the
 * negative-stock guard the server enforces.
 *
 * Refreshed (debounced) whenever the cart's SOLD lines change. Two things
 * come back per item: the on-hand level, shown as a chip on the cart line so
 * the cashier sees "3 left" before adding a fourth; and the shortages, which
 * the Charge button consults when the tenant blocks negative stock — so the
 * refusal happens at the cart, in the cashier's own language, and not as a
 * 422 after the customer has tapped their card.
 *
 * Silent on failure: a level the till cannot fetch is simply not shown, and
 * the server remains the gate. Never runs offline.
 */

export interface StockLevelEntry {
  onHand: number;
  unitName: string | null;
  unitNameAr: string | null;
}

/** `${itemId}:${unitId}` — the key `unitLevels` is stored under. */
export function unitLevelKey(itemId: number, unitId: number | null | undefined): string {
  return `${itemId}:${unitId ?? ''}`;
}

interface StockLevelsState {
  /** item id → level at the cashier's branch store, in the INVENTORY unit. */
  levels: Record<number, StockLevelEntry>;
  /**
   * `item:unit` → the same shelf counted in THAT unit. What a cart line shows:
   * `levels` is in the item's inventory unit, so a carton line sized against it
   * reads pieces and never warns (24 cartons "available" when 24 pieces are).
   */
  unitLevels: Record<string, StockLevelEntry>;
  shortages: StockShortageDto[];
  /** The last check's request signature, so an identical cart is not re-asked. */
  signature: string;
  checking: boolean;
  /** Shortages being SHOWN in the dialog (null = closed). */
  dialog: { shortages: StockShortageDto[]; blocking: boolean } | null;
  openDialog: (shortages: StockShortageDto[], blocking: boolean) => void;
  closeDialog: () => void;
  clear: () => void;
}

export const useStockLevels = create<StockLevelsState>((set) => ({
  levels: {},
  unitLevels: {},
  shortages: [],
  signature: '',
  checking: false,
  dialog: null,
  openDialog: (shortages, blocking) => set({ dialog: { shortages, blocking } }),
  closeDialog: () => set({ dialog: null }),
  clear: () => set({ levels: {}, unitLevels: {}, shortages: [], signature: '' }),
}));

/** Per-item quantities the SOLD lines add up to (a kit still counts as itself here). */
export function demandFromCart(
  lines: CartLine[],
): Array<{ item_id: number; unit_id?: number | null; qty: number }> {
  // One row per (item, unit): a carton line and a piece line of the same item
  // each convert by their own factor on the server, which sums them per item.
  const byKey = new Map<string, { item_id: number; unit_id: number | null; qty: number }>();
  const keyOf = (l: CartLine) => `${l.product.id}:${l.product.unitId ?? ''}`;
  for (const l of lines) {
    if (l.isReturn || l.giftCardIssue) continue;
    const k = keyOf(l);
    const cur = byKey.get(k);
    if (cur) cur.qty += l.qty;
    else byKey.set(k, { item_id: l.product.id, unit_id: l.product.unitId ?? null, qty: l.qty });
  }
  // Items on RETURN lines: level only (qty 0), so the chip still shows.
  for (const l of lines) {
    if (l.isReturn && ![...byKey.values()].some((d) => d.item_id === l.product.id)) {
      byKey.set(keyOf(l), { item_id: l.product.id, unit_id: l.product.unitId ?? null, qty: 0 });
    }
  }
  return [...byKey.values()];
}

let timer: ReturnType<typeof setTimeout> | null = null;
let inflight = 0;

/**
 * Ask the server for levels + shortages for the cart's current lines.
 * Debounced; a newer call supersedes an older one's result.
 */
export function refreshStockLevels(lines: CartLine[], delayMs = 350, editingReceiptId: number | null = null): void {
  if (timer) clearTimeout(timer);
  const demand = demandFromCart(lines);
  if (demand.length === 0) {
    useStockLevels.getState().clear();
    return;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  // The edited invoice is part of the question: its own lines go back on the
  // shelf before the comparison, so a different invoice is a different answer.
  const signature = `${editingReceiptId ?? ''}#` + demand.map((d) => `${d.item_id}:${d.unit_id ?? ''}:${d.qty}`).sort().join('|');
  if (signature === useStockLevels.getState().signature) return;

  timer = setTimeout(() => {
    const seq = ++inflight;
    useStockLevels.setState({ checking: true });
    stockAPI
      .check({
        lines: demand.map((d) => ({ item_id: d.item_id, unit_id: d.unit_id ?? null, qty: d.qty })),
        ...(editingReceiptId != null ? { exclude_receipt_id: editingReceiptId } : {}),
      })
      .then((res) => {
        if (seq !== inflight) return;
        const levels: Record<number, StockLevelEntry> = {};
        for (const l of res.levels) {
          levels[l.item_id] = { onHand: l.on_hand, unitName: l.unit_name, unitNameAr: l.unit_name_ar };
        }
        // Per (item, unit). Absent on an older API — the chip then falls back to
        // the per-item level, i.e. exactly what it showed before.
        const unitLevels: Record<string, StockLevelEntry> = {};
        for (const l of res.unit_levels ?? []) {
          unitLevels[unitLevelKey(l.item_id, l.unit_id)] = {
            onHand: l.on_hand,
            unitName: l.unit_name,
            unitNameAr: l.unit_name_ar,
          };
        }
        useStockLevels.setState({ levels, unitLevels, shortages: res.shortages, signature, checking: false });
        // The policy can change from the admin without a till reload; keep the
        // flag the cart reads in step with what the server just said.
        if (useTenantConfig.getState().allowNegativeStock !== res.allow_negative) {
          useTenantConfig.setState({ allowNegativeStock: res.allow_negative });
        }
      })
      .catch(() => {
        if (seq === inflight) useStockLevels.setState({ checking: false });
      });
  }, delayMs);
}
